import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { backofficeLinks } from "@/components/lib/SidebarLinks";
import { useState, useMemo, useEffect } from "react";
import Stepper from "@/components/common/stepper";
import { Form } from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import Step1 from "@/components/menu-management/burger/add-burger/step1";
import Step2 from "@/components/menu-management/burger/add-burger/step2";
import Step3 from "@/components/menu-management/burger/add-burger/step3";
import { useFetchAllMenuData } from "@/hooks/use-fetch-all-menu-data";
import useAuth from "@/hooks/useAuth";
import CreateMenuSkeleton from "@/components/skeleton/create-menu-skeleton";
import StepFormButtons from "@/components/common/step-for-button";
import { getFirstErrorMessage } from "@/lib/utils";
import {
  createNewBurger,
  updateExistingBurger,
} from "@/redux/slice/menu-manage-slice/burger-slice";
import { toast } from "react-hot-toast";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { setItemVariants } from "@/redux/slice/essential-menu-slice/variants-slice";
import { useStepFormHandler } from "@/hooks/useStepFormHandler";

export const burger_id = 6;

function CreateBurger() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { type } = useParams();
  const location = useLocation();
  const stepLabels = ["Step 1", "Step 2", "Step 3"];
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const item = location.state;

  const fetchOnly = useMemo(
    () => ["sauces", "categories", "allergens", "salads", "variants"],
    []
  );

  const { groupLoading } = useFetchAllMenuData(user.restaurant_id, burger_id, {
    fetchOnly,
  });

  const { createLoading, error, updateLoading } = useSelector(
    (state) => state.menuManage.burger
  );

  const form = useForm({
    resolver: zodResolver(createBurger),
    mode: "onChange",
    defaultValues: {
      type: "create",
      item_no: null,
      name: "",
      description: "",
      category_id: null,
      allergen_ids: [],
      spice_level: null,
      calorie: 0,
      size: "",
      vat: null,
      status: "active",
      file_manager_id: undefined,
      sauce_ids: [],
      salad_ids: [],
      variant_ids: [],
    },
  });
  const errorMessage = getFirstErrorMessage(form);

  const { step, nextStep, setStep, prevStep, entityId, loading } =
    useStepFormHandler({
      type,
      entity: "Burger",
      user,
      createAction: createNewBurger,
      updateAction: updateExistingBurger,
      totalSteps: stepLabels.length,
      item: item,
      toastSteps: { 0: false, 1: false, 2: true },
      successRedirectLink: "/backoffice/menu/burger",
    });

  useEffect(() => {
    if (type !== "update") return;
    if (!item) {
      navigate("/backoffice/menu/burger");
      return;
    }
    form.reset({
      type: "update",
      item_no: item.item_no || "",
      name: item.name || "",
      description: item.description || "",
      category_id: item.category_id || null,
      size: item.size || "",
      vat: item.vat ?? null,
      calorie: item.calorie ?? "",
      status: item.status || "active",
      file_manager_id: item.file_manager_id || undefined,
      salad_ids: item.salads?.map((i) => i.id) || [],
      sauce_ids: item.sauces?.map((i) => i.id) || [],
      allergen_ids: item.allergens?.map((a) => a.id) || [],
      variant_ids: item.variants?.map((v) => v.id) || [],
      spice_level: item.spice_level || null,
    });

    if (item.file_manager_id && item.file_manager_url != null) {
      setFiles([
        {
          file_manager_id: item?.file_manager_id,
          preview: item?.file_manager_url,
          name: item?.name,
        },
      ]);
    }
    dispatch(setItemVariants(item.variants));
  }, [type, item?.id, form]);

  if (groupLoading) return <CreateMenuSkeleton />;

  return (
    <Layout2
      pageName={{
        title: `${type == "update" ? "Update Burger" : "Add New Burger"}`,
      }}
      paths={[
        { name: "home", path: "/home" },
        { name: "Back Office", path: "/backoffice/overview" },
        { name: "Menu Management", path: "/backoffice/menu/drinks" },
        { name: "Burgers", path: "/backoffice/menu/burger" },
        {
          name: `${type == "update" ? "Update Burger" : "Add New Burger"}`,
          path: `/backoffice/menu/burger/${type}`,
        },
      ]}
      LeftBar={<LeftBar2 sidebarLinks={backofficeLinks} />}
    >
      <div className="py-5 container mt-5">
        <Stepper
          steps={stepLabels}
          onStepChange={setStep}
          type={form.watch("type")}
          currentStep={step}
        />
        {/* {errorMessage && (
          <div className="text-cred mb-2 text-sm mt-4 flex justify-center items-center text-center">
            {errorMessage}
          </div>
        )} */}
        <Form {...form}>
          <form>
            {step === 0 && <Step1 burger_id={burger_id} form={form} />}
            {step === 1 && (
              <Step2
                   item_id={entityId || item?.id}
                burger_id={burger_id}
                form={form}
              />
            )}
            {step === 2 && (
              <Step3 files={files} setFiles={setFiles} form={form} />
            )}

            <StepFormButtons
              step={step}
              totalSteps={stepLabels.length}
              isLoading={createLoading || updateLoading || loading}
              type={type}
              status={form.watch("status")}
              cancelLink="/backoffice/menu/burger"
              onBack={prevStep}
              onNext={async () => {
                const data = form.getValues();
                await nextStep("draft", data, form); // Step 1 & Step 2 → draft
              }}
              onSubmit={async () => {
                const data = form.getValues();
                await nextStep(data?.status, data, form); // Step 3 → active
              }}
              onDraftSave={async () => {
                const data = form.getValues();
                await nextStep("draft", data, form, { draftButton: true });
              }}
            />
          </form>
        </Form>
      </div>
    </Layout2>
  );
}

export default CreateBurger;

export const createBurger = z.object({
  item_no: z
    .preprocess(
      (val) => {
        if (val === "" || val == null) return null;
        return Number(val);
      },
      z
        .number({
          invalid_type_error: "Item no must be a number",
        })
        .nullable()
    )
    .optional(),

  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  category_id: z.preprocess((val) => {
    if (val === "" || val === undefined || val === null) return null;
    return Number(val);
  }, z.number().nullable().optional()),

  size: z.string().optional(),
  variant_ids: z
    .array(z.number(), {
      required_error: "Please select at least one variant",
    })
    .optional(),
  salad_ids: z.array(z.number()).optional(),
  sauce_ids: z.array(z.number()).optional(),
  allergen_ids: z.array(z.number()).optional(),
  calorie: z
    .preprocess(
      (val) => {
        if (val === "" || val == null) return 0;
        return Number(val);
      },
      z
        .number({
          invalid_type_error: "Calorie must be a number",
        })
        .nullable()
    )
    .optional(),
  spice_level: z
    .enum(["mild", "fairly_hot", "medium_hot", "very_hot", "extremely_hot"])
    .nullable()
    .default(null),
  vat: z
    .preprocess(
      (val) => (val === "" || val == null ? 0 : Number(val)),
      z.number({
        invalid_type_error: "VAT percentage must be a number",
      })
    )
    .refine((val) => val >= 0, {
      message: "VAT percentage cannot be negative",
    })
    .refine((val) => val <= 100, {
      message: "VAT percentage cannot exceed 100",
    })
    .optional(),
  file_manager_id: z
    .number({
      required_error: "Please upload an image",
      invalid_type_error: "File ID must be a number",
    })
    .int("File ID must be an integer")
    .optional(),
  status: z.enum(["active", "inactive", "draft"]).default("active"),
});
